import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { PlantApi } from 'apis/PlantApis';
import { RouteTrackingApi } from 'apis/RouteTrackingApi';
import { SaleLineApi } from 'apis/SaleLineApi';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { FormFilter, InputForm, SelectForm } from 'components';
import LazySelectForm, {
  ILazySelectData,
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import Content from 'layouts/Content';
import React, { memo, useEffect, useState } from 'react';

interface Iprops {
  isOpen: boolean;
  defaultFilter: any;
  onSearch: (values: any) => void;
  onCancel?: () => void;
}

const spanCol = 8;

const FilterEmployeeRouteTracking = (props: Iprops) => {
  const { user } = useUser();
  const { isOpen, defaultFilter, onSearch, onCancel } = props;
  const [form] = useForm();

  const [routes, setRoutes] = useState<ILazySelectData>(initLazySelectData);
  const [teams, setTeams] = useState<ILazySelectData>(initLazySelectData);
  const [status, setStatus] = useState<any>([]);
  const [plants, setPlants] = useState<any>([]);

  const mutateTeamSaleCbx = useMutation({
    mutationFn: SaleTeamApi.getAllTeamSaleCb,
    onSuccess: (res) => {
      const items = res.data?.getTeamSales?.items ?? [],
        totalItems = res.data?.getTeamSales?.pageInfo?.totalItems;

      const options =
        items.length > 0
          ? items.map((item: any) => ({
              label: `${item.subId} - ${item.name}`,
              value: item.id,
            }))
          : [];

      setTeams({
        options: options,
        totalItems: totalItems,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutateSaleLineCbx = useMutation({
    mutationFn: SaleLineApi.getAllRouteComboBox, //R100_01
    onSuccess: (res) => {
      const items = res.data?.getRouteM?.items ?? [],
        totalItems = res.data?.getRouteM?.pageInfo?.totalItems;

      const options =
        items.length > 0
          ? items.map((item: any) => ({
              label: `${item.id} - ${item.name}`,
              value: item.id,
            }))
          : [];

      setRoutes({
        options: options,
        totalItems: totalItems,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutateStatusCbx = useMutation({
    mutationFn: RouteTrackingApi.getStatusSalePlanning,
    onSuccess: (res: any) => {
      setStatus(
        (res?.data?.getCode?.items || []).map((item: any) => ({
          value: item.id,
          label: item.name,
        })),
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutatePlantCbx = useMutation({
    mutationFn: PlantApi.getAllPlantComboBox,
    onSuccess: (res: any) => {
      setPlants(
        (res?.data?.getPlant?.items || []).map((item: any) => ({
          value: item.id,
        })),
      );
    },
    onError: (error) => {
      console.log(error);
    },
  });

  useEffect(() => {
    mutateStatusCbx.mutate();
    if (user?.isAdmin || user?.isHO) {
      mutatePlantCbx.mutate();
      // mutateTeamSaleCbx.mutate({
      //   value: '',
      //   plantId: defaultFilter.plantId,
      //   pageSize: 10,
      // });
    }

    form.setFieldsValue({
      ...defaultFilter,
      plantId: defaultFilter?.plantId,
      teamSalesId: defaultFilter?.teamSaleId,
    });
  }, [user]);

  const handleRouteSelectScrollChange = (params: ILazySelectParams) => {
    const plant = form.getFieldValue('plantId');
    mutateSaleLineCbx.mutate({
      value: params.value,
      plantId: plant || defaultFilter.plantId,
      pageSize: params.pageSize,
    });
  };

  const handleTeamsSelectScrollChange = (params: ILazySelectParams) => {
    const plant = form.getFieldValue('plantId');
    mutateTeamSaleCbx.mutate({
      value: params.value,
      managerId: defaultFilter.managerId,
      plantId: plant || defaultFilter.plantId,
      pageSize: params.pageSize,
    });
  };

  const handleChangePlant = (value: any) => {
    form.setFieldsValue({
      teamSaleId: undefined,
      routeId: undefined,
    });
    mutateTeamSaleCbx.mutate({
      value: '',
      plantId: value,
      pageSize: 10,
    });

    mutateSaleLineCbx.mutate({
      value: '',
      plantId: value,
      pageSize: 10,
    });
  };

  const onFinish = (values: any) => {
    onSearch(values);
  };

  const handlCancel = () => {
    form.resetFields();
    form.setFieldsValue({
      ...defaultFilter,
      ...((user?.isAdmin || user?.isHO) && { plantId: plants[0]?.value }),
    });

    // onCancel();
    onSearch({
      ...defaultFilter,
      ...((user?.isAdmin || user?.isHO) && { plantId: plants[0]?.value }),
    });
  };

  return (
    <Content isOpen={isOpen} style={{ marginTop: 0, paddingTop: 0 }}>
      <FormFilter
        onFinish={onFinish}
        onCancel={handlCancel}
        form={form}
        customClass="mt-0"
      >
        <Row gutter={[10, 0]}>
          <Col
            // span={spanCol}
            className="h-[80px] w-1/3 min-w-[165px] sm:w-1/2"
          >
            <Form.Item label="Ngày" name="implementationDate">
              <DatePicker
                format={'DD/MM/YYYY'}
                style={{ width: '100%' }}
                size="large"
                placeholder="Chọn ngày"
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col
            // span={spanCol}
            className="h-[80px] w-1/3 min-w-[165px] sm:w-1/2"
          >
            <Form.Item label="Chi nhánh" name="plantId">
              <SelectForm
                allowClear={false}
                placeholder="Chọn chi nhánh"
                disabled={user?.isSale || user?.isCN}
                options={plants}
                onChange={handleChangePlant}
              />
            </Form.Item>
          </Col>
          <Col
            // span={spanCol}
            className="h-[80px] w-1/3 min-w-[165px] sm:w-1/2"
          >
            <Form.Item label="Đội bán hàng" name="teamSaleId">
              <LazySelectForm
                data={teams}
                loading={mutateSaleLineCbx.isPending}
                onSelectScrollChange={handleTeamsSelectScrollChange}
                placeholder="Chọn đội"
                disabled={user?.isSale}
              />
            </Form.Item>
          </Col>
          <Col
            // span={spanCol}
            className="h-[80px] w-1/3 min-w-[165px] sm:w-1/2"
          >
            <Form.Item label="Tuyến bán hàng" name="routeId">
              <LazySelectForm
                data={routes}
                loading={mutateSaleLineCbx.isPending}
                placeholder="Chọn tuyến"
                onSelectScrollChange={handleRouteSelectScrollChange}
              />
            </Form.Item>
          </Col>
          <Col
            // span={spanCol}
            className="h-[80px] w-1/3 min-w-[165px] sm:w-1/2 "
          >
            <Form.Item label="Khách hàng" name="customerId">
              <InputForm placeholder="Mã khách hàng,DMS,MST" />
            </Form.Item>
          </Col>
          <Col
            // span={spanCol}
            className="h-[80px] w-1/3 min-w-[165px] sm:w-1/2 "
          >
            <Form.Item label="Trạng thái" name="statusId">
              <SelectForm placeholder="Chọn trạng thái" options={status} />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
};

export default memo(FilterEmployeeRouteTracking);
